import { HttpClient } from '../api/client'
import { deduplicateRequest } from '../api/deduplicateRequest'
import { TokenManager } from '../auth/tokenManager'
import type {
  ApiResponse,
  ProfileEndpoints,
  CreditQuotaInfo,
  CreditUsageResponse,
} from '@bandos/shared'

const dedupedGet = deduplicateRequest(
  async () => {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get<ProfileEndpoints['get']['response']>('/profile', headers)
  },
  { keyFn: () => 'profile' }
)

const dedupedGetFeatures = deduplicateRequest(
  async () => {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get<UserFeatureAccess>('/profile/features', headers)
  },
  { keyFn: () => 'features' }
)

export class ProfileService {
  /**
   * Get current user's profile
   */
  static get(): Promise<ApiResponse<ProfileEndpoints['get']['response']>> {
    return dedupedGet()
  }

  /**
   * Update user profile
   */
  static async update(
    profileData: ProfileEndpoints['update']['body']
  ): Promise<ApiResponse<ProfileEndpoints['update']['response']>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.put<ProfileEndpoints['update']['response']>('/profile', profileData, headers)
  }

  /**
   * Get current user's project quota info
   */
  static async getProjectQuota(): Promise<
    ApiResponse<{
      currentCount: number
      projectLimit: number
      canCreate: boolean
      isUnlimited: boolean
    }>
  > {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get('/profile/project-quota', headers)
  }

  /**
   * Get current user's storyboard quota info
   */
  static async getStoryboardQuota(): Promise<
    ApiResponse<{
      currentCount: number
      storyboardLimit: number
      canGenerate: boolean
      isUnlimited: boolean
    }>
  > {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get('/profile/storyboard-quota', headers)
  }

  /**
   * Get current user's monthly survey creation quota
   */
  static async getSurveyQuota(): Promise<
    ApiResponse<{
      currentCount: number
      monthlyLimit: number
      canCreate: boolean
      isUnlimited: boolean
    }>
  > {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get('/profile/survey-quota', headers)
  }

  /**
   * Get current user's monthly interview creation quota
   */
  static async getInterviewQuota(): Promise<
    ApiResponse<{
      currentCount: number
      monthlyLimit: number
      canCreate: boolean
      isUnlimited: boolean
    }>
  > {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get('/profile/interview-quota', headers)
  }

  /**
   * Get current user's feature access based on subscription tier
   */
  static getFeatures(): Promise<ApiResponse<UserFeatureAccess>> {
    return dedupedGetFeatures()
  }

  /**
   * Get current user's credit quota info
   */
  static async getCreditQuota(): Promise<ApiResponse<CreditQuotaInfo>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get('/profile/credit-quota', headers)
  }

  /**
   * Get current user's credit usage history
   */
  static async getCreditUsage(params?: {
    limit?: number
    offset?: number
    startDate?: string
    endDate?: string
  }): Promise<ApiResponse<CreditUsageResponse>> {
    const headers = await TokenManager.getAuthHeaders()
    const queryParams = new URLSearchParams()

    if (params?.limit !== undefined) {
      queryParams.append('limit', params.limit.toString())
    }
    if (params?.offset !== undefined) {
      queryParams.append('offset', params.offset.toString())
    }
    if (params?.startDate) {
      queryParams.append('startDate', params.startDate)
    }
    if (params?.endDate) {
      queryParams.append('endDate', params.endDate)
    }

    const queryString = queryParams.toString()
    const url = queryString ? `/profile/credit-usage?${queryString}` : '/profile/credit-usage'

    return HttpClient.get(url, headers)
  }

  /**
   * Get tier comparison data for upgrade modal (public endpoint, no auth required)
   */
  static async getTierComparison(): Promise<ApiResponse<TierComparisonData>> {
    // Public endpoint - no auth headers needed
    return HttpClient.get('/public/tiers/comparison')
  }

  /**
   * Mark user's onboarding as complete (for plan selection flow)
   */
  static async markOnboardingComplete(): Promise<ApiResponse<{ success: boolean }>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.post('/profile/onboarding-complete', {}, headers)
  }
}

export interface GlobalToggles {
  simulated_stakeholders: boolean
  node_quality_gate: boolean
  labels: boolean
  auto_chain_suggestions: boolean
}

export type GlobalToggleName = keyof GlobalToggles

export interface UserFeatureAccess {
  tier: 'free' | 'paid'
  features: {
    pdf_export: boolean
    figma_prompt: boolean
  }
  onboardingCompleted: boolean
  globalToggles: GlobalToggles
}

/**
 * Tier settings structure matching backend TierSettings
 */
export interface TierSettings {
  // Pricing
  price_monthly: number // Price in dollars (0 = free)
  price_period: string // Display text: "forever", "month", "year", etc.
  trial_days: number // Free trial period in days (0 = no trial)
  // Limits
  token_limit_monthly: number
  project_limit: number
  storyboard_limit: number
  credit_limit_monthly: number
  // Features
  pdf_export: boolean
  figma_prompt: boolean
  /** Human-readable display labels for each setting key */
  labels?: Partial<Record<string, string>>
  /** Tooltip descriptions for each setting key, shown in the comparison table */
  tooltips?: Partial<Record<string, string>>
}

/**
 * Response from tier comparison endpoint
 */
export interface TierComparisonData {
  free: TierSettings
  paid: TierSettings
}

// Re-export credit types from shared for convenience
export type { CreditQuotaInfo, CreditTransaction, CreditUsageResponse } from '@bandos/shared'
